import { Box } from '@chakra-ui/react';
import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

import useMfaCode from '../useMfaCode';

import { LoginMfaForm } from '@/v5/components/organisms/AuthForm/LoginForm/LoginMfaForm';

export default function LoginMfa({
    username,
    password,
    cancelLogin,
    setSubmitting,
    setValues,
    setTouched,
}) {
    const dispatch = useDispatch();

    const {
        mfaCode,
        mfaCodeObj,
        shouldWeSwapToNextInput,
        shouldWeGoToPreviousInput,
        mfaCodeIndexDisabled,
        refs,
        hasEnteredCode,
    } = useMfaCode();

    const [mfaCodeError, setMfaCodeError] = useState(null);
    const [loading, setLoading] = useState(false);
    const isInvalid = mfaCode?.length !== 6;

    function handleCancel() {
        setSubmitting(false);
        setValues((prev) => ({ ...prev, password: '' }));
        setTouched((prev) => ({ ...prev, password: false }));
        cancelLogin();
    }

    function onLoginWithMfaCode(e) {
        e.preventDefault();
        mfaLogin();
    }

    function mfaLogin() {
        setLoading(true);
        fetch(`${import.meta.env.VITE_APP_API_URL}/auth`, {
            headers: {
                accept: 'application/json, text/plain, */*',
                'Content-Type': 'application/json;',
                'x-requestscope': '20',
            },
            method: 'POST',
            mode: 'cors',
            credentials: 'omit',
            body: JSON.stringify({ code: mfaCode, username, password }),
        }).then(async (response) => {
            const data = await response.json();
            if (data.status === 401) {
                setLoading(false);
                if (data.title === 'Unauthorized') {
                    setMfaCodeError(true);
                }
            } else {
                setMfaCodeError(false);

                fetch(`${import.meta.env.VITE_APP_API_URL}/AdminUser/me`, {
                    headers: {
                        accept: 'application/json, text/plain, */*',
                        authorization: `bearer ${data.token}`,
                        'x-requestscope': '20',
                    },
                    method: 'GET',
                    mode: 'cors',
                    credentials: 'omit',
                })
                    .then(async (adminResponse) => {
                        const adminData = await adminResponse.json();

                        dispatch({
                            type: 'LOGIN_SUCCESS',
                            payload: {
                                ...data,
                                ...adminData,
                                loadAccessList: true,
                                email: data?.user,
                            },
                        });
                        toast.success('You have been logged in successfully.');
                    })
                    .catch((error) => {
                        console.error(
                            'Failed to fetch admin user data:',
                            error,
                        );
                        toast.error('Failed to load user information');
                    })
                    .finally(() => {
                        setLoading(false);
                    });
            }
        });
    }

    useEffect(() => {
        if (hasEnteredCode) {
            mfaLogin();
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps */
    }, [hasEnteredCode]);

    useEffect(() => {
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Backspace' && isInvalid) {
                setMfaCodeError(null);
            }
        });
    }, [isInvalid]);

    return (
        <Box onSubmit={onLoginWithMfaCode}>
            <Box padding="20px 0px">
                <LoginMfaForm
                    mfaCodeObj={mfaCodeObj}
                    mfaCodeError={mfaCodeError}
                    shouldWeSwapToNextInput={shouldWeSwapToNextInput}
                    shouldWeGoToPreviousInput={shouldWeGoToPreviousInput}
                    mfaCodeIndexDisabled={mfaCodeIndexDisabled}
                    refs={refs}
                    hasEnteredCode={hasEnteredCode}
                    isInvalid={isInvalid}
                    loading={loading}
                    onCancel={handleCancel}
                />
            </Box>
        </Box>
    );
}
